/** Access group template groups — system-wide catalog (Ops only).
 *
 * Server-driven grid + create/edit dialog + per-row "Materialise for tenant".
 */
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { getGridStringOperators, type GridColDef } from '@mui/x-data-grid-pro';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { rbacApi, type AgroupTemplateGroup } from '@/api/rbac';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { PageHeader } from '@/components/PageHeader';
import { PermissionGate } from '@/components/PermissionGate';
import { useSnackbar } from '@/components/Snackbar';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { usePermissions } from '@/hooks/usePermissions';

import { MaterialiseDialog } from './MaterialiseDialog';

const containsOnly = getGridStringOperators().filter((op) => op.value === 'contains');

/** URL-backed header filters (migrated from the grid-internal filterFieldMap
 *  so presets and deep links can carry them). */
const FILTERS: FilterDef[] = [
  { param: 'name_contains', column: { field: 'name', operator: 'contains' } },
  { param: 'description_contains', column: { field: 'description', operator: 'contains' } },
];

const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

/** Column field order (mirrors the columns memo — keep in sync). */
const FIELD_ORDER = [
  'name',
  'description',
  'template_count',
  'materialised_tenant_count',
  'updated_ts',
  'created_ts',
  '__actions__',
];

export function TemplateGroupsListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_rbac_manage');
  const snackbar = useSnackbar();
  const qc = useQueryClient();

  const [editing, setEditing] = useState<AgroupTemplateGroup | 'new' | null>(null);
  const [materialising, setMaterialising] = useState<AgroupTemplateGroup | null>(null);
  const [deleting, setDeleting] = useState<AgroupTemplateGroup | null>(null);

  const presets = useGridPresets({
    grid: 'template_groups',
    defaultOrder: FIELD_ORDER,
    defaultHidden: ['created_ts'],
    defaultOrdering: 'name',
    filters: PRESET_FILTERS,
  });
  const gf = useGridFilters(FILTERS, presets.filtersLayer);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => rbacApi.deleteGroup(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agroupTemplateGroups'] });
      snackbar.show('Template group deleted', 'success');
    },
    onError: (e) => {
      const msg =
        e instanceof ConsoleApiError && e.status === 409
          ? 'Templates still belong to this group — delete them first.'
          : (e as Error).message;
      snackbar.show(msg, 'error');
    },
  });

  const columns = useMemo<GridColDef<AgroupTemplateGroup>[]>(
    () => [
      { field: 'name', headerName: 'Name', flex: 1.4, filterOperators: containsOnly },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        sortable: false,
        filterOperators: containsOnly,
      },
      // Computed (SerializerMethodField) — not in the backend ordering_fields, so a
      // header sort would silently no-op. Keep display-only.
      { field: 'template_count', headerName: 'Templates', width: 110, filterable: false, sortable: false },
      {
        field: 'materialised_tenant_count',
        headerName: 'Tenants',
        width: 100,
        sortable: false,
        filterable: false,
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 170,
        filterable: false,
        valueFormatter: (value?: string) => (value ? new Date(value).toLocaleString() : ''),
      },
      {
        // created_ts is in the backend ordering_fields, so header sort round-trips.
        field: 'created_ts',
        headerName: 'Created',
        width: 170,
        filterable: false,
        valueFormatter: (value?: string | null) => (value ? new Date(value).toLocaleString() : ''),
      },
      {
        field: '__actions__',
        headerName: '',
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Stack direction="row" spacing={0.5}>
            <PermissionGate perm="console_rbac_manage">
              <Tooltip title="Materialise for tenant…">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMaterialising(p.row);
                  }}
                >
                  <PlayArrowIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleting(p.row);
                }}
              >
                Delete
              </Button>
            </PermissionGate>
          </Stack>
        ),
      },
    ],
    [],
  );

  const queryFn = (params: ResourceDataGridParams) => {
    const extra = params.extra ?? {};
    return rbacApi.listGroups({
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      ordering: params.ordering ?? presets.ordering,
      name_contains: extra.name_contains ? String(extra.name_contains) : undefined,
      description_contains: extra.description_contains
        ? String(extra.description_contains)
        : undefined,
    });
  };

  return (
    <>
      <PageHeader
        helpStory="rbac-model"
        title="Access Group Template Groups"
        subtitle="System-wide template bundles, materialised per tenant into managed access groups."
        actions={
          <PermissionGate perm="console_rbac_manage">
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
              New group
            </Button>
          </PermissionGate>
        }
      />
      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<AgroupTemplateGroup & { id: number }>
          queryKey={['agroupTemplateGroups']}
          queryFn={queryFn}
          columns={orderColumns(
            columns.map((c) =>
              c.field === 'name' && canManage
                ? {
                    ...c,
                    renderCell: (p) => (
                      <Button
                        size="small"
                        sx={{ textTransform: 'none' }}
                        onClick={() => setEditing(p.row)}
                      >
                        {p.row.name}
                      </Button>
                    ),
                  }
                : c,
            ),
            presets.orderedFields,
          )}
          extra={gf.extra}
          filterModel={gf.filterModel}
          onFilterModelChange={gf.onFilterModelChange}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          headerToolbar={{
            search: true,
            searchPlaceholder: 'Search template groups',
            exportFileName: 'template-groups',
            onResetAllFilters: gf.resetAll,
            resetDisabled: !gf.anyActive,
            presets: presets.toolbar,
          }}
        />
      )}

      {editing !== null && (
        <GroupEditDialog
          group={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {materialising !== null && (
        <MaterialiseDialog group={materialising} onClose={() => setMaterialising(null)} />
      )}
      <ConfirmDialog
        open={deleting !== null}
        title="Delete template group?"
        message={`Delete "${deleting?.name ?? ''}"? This is only possible while no templates belong to it.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (deleting) deleteMutation.mutate(deleting.id);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}

function GroupEditDialog({
  group,
  onClose,
}: {
  group: AgroupTemplateGroup | null;
  onClose: () => void;
}) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');

  const mutation = useMutation({
    mutationFn: () =>
      group
        ? rbacApi.updateGroup(group.id, { name, description })
        : rbacApi.createGroup({ name, description }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agroupTemplateGroups'] });
      snackbar.show(group ? 'Template group updated' : 'Template group created', 'success');
      onClose();
    },
    onError: (e) => snackbar.show((e as Error).message, 'error'),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{group ? `Edit "${group.name}"` : 'New template group'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            inputProps={{ autoComplete: 'off' }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
